
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { Box, Stack, Typography } from "@mui/material";

import {
  HOPE_SANS,
  HOPE_SERIF,
} from "@/shared/branding/thehopeonebrand";

import type {
  ImpactCardData,
  ImpactFundRecipient,
} from "../utils/impactCardData";

import { mergeFundRecipients } from "../utils/impactCardData";
import { formatImpactMoney } from "../utils/impactCurrency";

const c = {
  forestDeep: "#102C24",
  forest: "#1D4437",
  green: "#2F6B4F",
  greenMist: "#E7F0E8",
  gold: "#A9812F",
  goldMist: "#F6EBD4",
  muted: "#6B7A73",
  paper: "#FFFFFF",
  ivory: "#FAF8F2",
  line: "rgba(16,44,36,.08)",
  track: "#E4EDE6",
};

type ImpactFundingPageProps = {
  data: ImpactCardData;
  title?: string;
  description?: string;
  coverImageUrl?: string;
  onNavigate?: (
    section: "recipientPm" | "recipientAol" | "contribute"
  ) => void;
};

function ProgressBar({ value }: { value: number }) {
  const width = Math.max(0, Math.min(100, value));

  return (
    <Box
      role="progressbar"
      aria-label="모금 목표 달성률"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(width)}
      sx={{
        height: { xs: 5, sm: 7 },
        borderRadius: 99,
        bgcolor: c.track,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: `${width}%`,
          height: "100%",
          borderRadius: 99,
          background: `linear-gradient(90deg, ${c.forestDeep}, ${c.green})`,
          transition: "width .3s ease",
        }}
      />
    </Box>
  );
}

function MoneyStat({
  label,
  value,
  align = "left",
}: {
  label: string;
  value: number;
  align?: "left" | "right";
}) {
  return (
    <Box sx={{ minWidth: 0, textAlign: align }}>
      <Typography
        sx={{
          fontFamily: HOPE_SANS,
          fontSize: { xs: "0.66rem", sm: "0.76rem" },
          color: c.muted,
          mb: 0.35,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontFamily: HOPE_SERIF,
          fontWeight: 700,
          fontSize: { xs: "1.04rem", sm: "1.4rem" },
          lineHeight: 1.2,
          color: c.forestDeep,
          fontVariantNumeric: "tabular-nums",
          overflowWrap: "anywhere",
        }}
      >
        {formatImpactMoney(value)}
      </Typography>
    </Box>
  );
}

function RecipientRow({
  recipient,
  onOpen,
}: {
  recipient: ImpactFundRecipient;
  onOpen?: () => void;
}) {
  const isAol = recipient.sectionKey === "recipientAol";
  const accent = isAol ? c.gold : c.green;

  const shortLabel = isAol
    ? "지역사회 회복 지원"
    : "긴급 재난 구호";

  const shortIntro =
    recipient.blurb ||
    recipient.description ||
    recipient.tagline;

  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={`${recipient.name} 자세히 보기`}
      sx={{
        width: "100%",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        textAlign: "left",
        gap: { xs: 1.1, sm: 1.5 },
        px: { xs: 0.25, sm: 1.75 },
        py: { xs: 1.05, sm: 1.75 },
        bgcolor: "transparent",
        border: 0,
        borderRadius: { xs: 0, sm: "14px" },
        cursor: onOpen ? "pointer" : "default",
        color: c.forestDeep,
        font: "inherit",
        transition: "background-color .18s ease",
        "&:hover": {
          bgcolor: onOpen ? c.ivory : "transparent",
        },
        "&:focus-visible": {
          outline: `2px solid ${accent}`,
          outlineOffset: 3,
        },
      }}
    >
      {/* Compact organization symbol */}
      <Box
        aria-hidden="true"
        sx={{
          width: { xs: 35, sm: 46 },
          height: { xs: 35, sm: 46 },
          flexShrink: 0,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: isAol ? c.goldMist : c.greenMist,
          color: accent,
          fontFamily: HOPE_SANS,
          fontWeight: 800,
          fontSize: isAol
            ? { xs: "0.58rem", sm: "0.68rem" }
            : { xs: "1.05rem", sm: "1.25rem" },
        }}
      >
        {isAol ? "AOL" : "🇳🇵"}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: HOPE_SANS,
            fontWeight: 800,
            fontSize: { xs: "0.81rem", sm: "1rem" },
            lineHeight: 1.35,
            color: c.forestDeep,
            wordBreak: "keep-all",
          }}
        >
          {recipient.name}
        </Typography>

        {/* Mobile: one short line only */}
        <Typography
          sx={{
            mt: 0.2,
            fontFamily: HOPE_SANS,
            fontSize: { xs: "0.68rem", sm: "0.76rem" },
            lineHeight: 1.45,
            color: c.muted,
          }}
        >
          {shortLabel}
        </Typography>

        {/* Desktop: additional description */}
        {shortIntro ? (
          <Typography
            sx={{
              display: {
                xs: "none",
                sm: "-webkit-box",
              },
              mt: 0.6,
              fontFamily: HOPE_SANS,
              fontSize: "0.8rem",
              lineHeight: 1.55,
              color: c.muted,
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              wordBreak: "keep-all",
            }}
          >
            {shortIntro}
          </Typography>
        ) : null}
      </Box>

      <ArrowForwardIosRoundedIcon
        sx={{
          fontSize: { xs: 13, sm: 15 },
          color: c.muted,
          flexShrink: 0,
        }}
      />
    </Box>
  );
}

export default function ImpactFundingPage({
  data,
  description,
  coverImageUrl,
  onNavigate,
}: ImpactFundingPageProps) {
  const verified = data.verifiedFund ?? 0;
  const goal = data.targetAmount ?? 0;
  const remaining = Math.max(0, goal - verified);

  const progress =
    goal > 0
      ? Math.min(100, (verified / goal) * 100)
      : 0;

  const recipients = mergeFundRecipients(
    data.fundRecipients
  );

  const orgCount = (data.contributors ?? []).filter(
    (item) => /org/i.test(item.accountType || "")
  ).length;

  const goalStatement =
    data.impactGoal?.trim() ||
    description?.trim() ||
    "네팔의 재난 피해 주민을 위한 구호와 회복을 지원합니다.";

  return (
    <Stack
      spacing={{ xs: 1.25, sm: 2.5 }}
      sx={{
        width: "100%",
        minWidth: 0,
        pb: { xs: 1, sm: 2 },
      }}
    >
      {/* ============================================================= */}
      {/* Funding summary                                               */}
      {/* ============================================================= */}

      <Box
        sx={{
          position: "relative",
          mx: { xs: -2.25, sm: -3 },
          mt: { xs: -2.5, sm: -3.25 },
          px: { xs: 2.25, sm: 3 },
          pt: { xs: 1.65, sm: 3 },
          pb: { xs: 1.5, sm: 2.5 },
          overflow: "hidden",
          ...(coverImageUrl
            ? {
                backgroundImage: `linear-gradient(
                  180deg,
                  rgba(250,248,242,.92) 0%,
                  rgba(250,248,242,.98) 100%
                ), url(${coverImageUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : {
                bgcolor: c.ivory,
              }),
        }}
      >
        <Typography
          sx={{
            fontFamily: HOPE_SANS,
            fontSize: { xs: "0.59rem", sm: "0.68rem" },
            fontWeight: 800,
            letterSpacing: "0.1em",
            color: c.green,
          }}
        >
          네팔을 위한 함께하는 나눔
        </Typography>

        <Typography
          sx={{
            mt: { xs: 0.35, sm: 0.7 },
            fontFamily: HOPE_SERIF,
            fontWeight: 700,
            fontSize: { xs: "1.48rem", sm: "2.35rem" },
            lineHeight: 1.2,
            letterSpacing: "-0.025em",
            color: c.forestDeep,
          }}
        >
          모금 목표
        </Typography>

        {/* Full description on desktop, short summary on mobile */}
        <Typography
          sx={{
            mt: { xs: 0.35, sm: 0.8 },
            maxWidth: 620,
            fontFamily: HOPE_SANS,
            fontSize: { xs: "0.75rem", sm: "0.88rem" },
            lineHeight: { xs: 1.45, sm: 1.7 },
            color: c.muted,
            wordBreak: "keep-all",
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: { xs: 2, sm: 4 },
            overflow: "hidden",
          }}
        >
          {goalStatement}
        </Typography>

        {/* Goal and progress */}
        <Box sx={{ mt: { xs: 1.2, sm: 2.1 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: HOPE_SANS,
                  fontSize: "0.66rem",
                  color: c.muted,
                  mb: 0.2,
                }}
              >
                전체 모금 목표
              </Typography>

              <Typography
                sx={{
                  fontFamily: HOPE_SERIF,
                  fontWeight: 700,
                  fontSize: { xs: "1.6rem", sm: "2.6rem" },
                  lineHeight: 1.1,
                  color: c.forestDeep,
                  fontVariantNumeric: "tabular-nums",
                  overflowWrap: "anywhere",
                }}
              >
                {formatImpactMoney(goal)}
              </Typography>
            </Box>

            <Typography
              sx={{
                flexShrink: 0,
                fontFamily: HOPE_SANS,
                fontSize: { xs: "0.72rem", sm: "0.9rem" },
                fontWeight: 800,
                color: c.green,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {progress.toFixed(0)}%
            </Typography>
          </Stack>

          <Box sx={{ mt: { xs: 0.75, sm: 1.15 } }}>
            <ProgressBar value={progress} />
          </Box>
        </Box>

        {/* Essential financial information */}
        <Box
          sx={{
            mt: { xs: 1.1, sm: 1.8 },
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: { xs: 1, sm: 2 },
          }}
        >
          <MoneyStat
            label="입금 확인 금액"
            value={verified}
          />

          <MoneyStat
            label="남은 목표 금액"
            value={remaining}
            align="right"
          />
        </Box>

        {/* Additional statistics on desktop only */}
        <Stack
          direction="row"
          spacing={2.5}
          sx={{
            display: { xs: "none", sm: "flex" },
            mt: 1.75,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.65}
          >
            <GroupsOutlinedIcon
              sx={{ fontSize: 17, color: c.green }}
            />

            <Typography
              sx={{
                fontFamily: HOPE_SANS,
                fontSize: "0.77rem",
                color: c.muted,
              }}
            >
              확인된 기부자{" "}
              <Box
                component="span"
                sx={{ fontWeight: 800, color: c.forestDeep }}
              >
                {data.contributorCount ?? 0}명
              </Box>
            </Typography>
          </Stack>

          {/* <Stack
            direction="row"
            alignItems="center"
            spacing={0.65}
          >
            <ApartmentOutlinedIcon
              sx={{ fontSize: 17, color: c.green }}
            />

            <Typography
              sx={{
                fontFamily: HOPE_SANS,
                fontSize: "0.77rem",
                color: c.muted,
              }}
            >
              단체 기부자{" "}
              <Box
                component="span"
                sx={{ fontWeight: 800, color: c.forestDeep }}
              >
                {orgCount}곳
              </Box>
            </Typography>
          </Stack> */}
        </Stack>
      </Box>

      {/* ============================================================= */}
      {/* Recipient organizations                                        */}
      {/* ============================================================= */}

      <Box sx={{ pt: { xs: 0.25, sm: 0.75 } }}>
        <Typography
          sx={{
            fontFamily: HOPE_SERIF,
            fontWeight: 700,
            fontSize: { xs: "1.05rem", sm: "1.65rem" },
            color: c.forestDeep,
            lineHeight: 1.3,
            mb: { xs: 0.45, sm: 0.7 },
          }}
        >
          후원금이 전달되는 곳
        </Typography>

        <Typography
          sx={{
            display: { xs: "none", sm: "block" },
            maxWidth: 650,
            fontFamily: HOPE_SANS,
            fontSize: "0.85rem",
            lineHeight: 1.7,
            color: c.muted,
            mb: 1.5,
          }}
        >
          두 기관을 통해 네팔의 긴급 재난 구호와
          지역사회 회복을 지원합니다. 각 기관을
          선택하면 소개와 활동 사진 및 영상을
          확인할 수 있습니다.
        </Typography>

        {/* Mobile: two compact rows.
            Desktop: two wider cards without heavy borders. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: { xs: 0, sm: 1.5 },
            "& > * + *": {
              borderTop: {
                xs: `1px solid ${c.line}`,
                sm: "none",
              },
            },
          }}
        >
          {recipients.map((recipient) => (
            <RecipientRow
              key={recipient.key}
              recipient={recipient}
              onOpen={
                onNavigate
                  ? () =>
                      onNavigate(
                        recipient.sectionKey === "recipientAol"
                          ? "recipientAol"
                          : "recipientPm"
                      )
                  : undefined
              }
            />
          ))}
        </Box>
      </Box>

      {/* Closing message: desktop only to keep mobile concise */}
      <Typography
        sx={{
          display: { xs: "none", sm: "block" },
          pt: 1,
          textAlign: "center",
          fontFamily: HOPE_SERIF,
          fontStyle: "italic",
          fontSize: "1rem",
          lineHeight: 1.65,
          color: c.forest,
        }}
      >
        “따뜻한 마음과 실천이 모일 때,
        지역사회는 다시 일어설 힘을 얻습니다.”
      </Typography>
    </Stack>
  );
}